import { EditorNS } from '@marpple/sticker-editor';

/**
 * size_guide 갱신
 * @param {object} params
 * @param {HTMLDivElement} params.tab_el
 * @param {import("@marpple/sticker-editor").EditorNS.AcrylicCuttingLineEditorNS.PCNS.AcrylicCuttingLinePCEditor |
 * import("@marpple/sticker-editor").EditorNS.AcrylicCuttingLineEditorNS.MobileNS.AcrylicCuttingLineMobileEditor |
 * import("@marpple/sticker-editor").EditorNS.AcrylicFigureStandLegEditorNS.PCNS.AcrylicFigureStandLegPCEditor |
 * import("@marpple/sticker-editor").EditorNS.AcrylicFigureStandLegEditorNS.MobileNS.AcrylicFigureStandLegMobileEditor |
 * import("@marpple/sticker-editor").EditorNS.CuttingLineEditorNS.PCNS.CuttingLinePCEditor |
 * import("@marpple/sticker-editor").EditorNS.CuttingLineEditorNS.MobileNS.CuttingLineMobileEditor} params.editor
 * @returns {void}
 */
export const setSizeGuide = (params) => {
  const { tab_el, editor } = params;

  const foreground_container_el = tab_el.querySelector(`.foreground_container`) ?? null;
  if (!foreground_container_el) return;

  const size_guide_container_el = foreground_container_el.querySelector(`.size_guide_container`) ?? null;
  const size_guide_container_value_el =
    size_guide_container_el?.querySelector(`.size_guide_container_value`) ?? null;

  // 스티커(CuttingLinePCEditor, CuttingLineMobileEditor) 를 제외한 나머지 벡터 에디터(아크릴, 스탠드, 키링) 분리
  const outer_cutting_line_path_el =
    editor instanceof EditorNS.CuttingLineEditorNS.PCNS.CuttingLinePCEditor ||
    editor instanceof EditorNS.CuttingLineEditorNS.MobileNS.CuttingLineMobileEditor
      ? editor.getCuttingLinePathEl()
      : editor.getOuterCuttingLinePathEl();
  const outer_cutting_line_path_el_bbox = outer_cutting_line_path_el.getBBox();
  const size_width = Math.floor(outer_cutting_line_path_el_bbox.width);
  const size_height = Math.floor(outer_cutting_line_path_el_bbox.height);
  const size_value = `${size_width} x ${size_height} mm`;

  const left_container_el = foreground_container_el.closest('.left_container');
  const boundary_rect = left_container_el
    ? left_container_el.getBoundingClientRect()
    : { width: window.innerWidth, height: window.innerHeight };

  const foreground_container_rect = foreground_container_el.getBoundingClientRect();
  const size_guide_container_value_rect = size_guide_container_value_el?.getBoundingClientRect() ?? {
    width: 0,
    height: 0,
  };

  const top_left_xy = editor.convertCoordsToOutside({
    x: outer_cutting_line_path_el_bbox.x,
    y: outer_cutting_line_path_el_bbox.y,
  });
  const bottom_right_xy = editor.convertCoordsToOutside({
    x: outer_cutting_line_path_el_bbox.x + outer_cutting_line_path_el_bbox.width,
    y: outer_cutting_line_path_el_bbox.y + outer_cutting_line_path_el_bbox.height,
  });
  const width = bottom_right_xy.x - top_left_xy.x;
  const x = Math.max(
    Math.min(
      top_left_xy.x - foreground_container_rect.x + width / 2 - size_guide_container_value_rect.width / 2,
      boundary_rect.width - size_guide_container_value_rect.width,
    ),
    0,
  );
  const y = Math.max(
    top_left_xy.y - foreground_container_rect.y - size_guide_container_value_rect.height * 2,
    64, // 48(모바일 헤더) + 16(마진)
  );

  updateSizeGuideValue(size_guide_container_value_el, { x, y }, size_value, true);
};

/**
 * 공통 로직: size_guide_container_value_el 업데이트
 * @param {HTMLElement} size_guide_container_value_el
 * @param {object} position
 * @param {number} position.x
 * @param {number} position.y
 * @param {string} size_value
 * @param {boolean} is_visible
 */
const updateSizeGuideValue = (size_guide_container_value_el, position, size_value, is_visible) => {
  if (!size_guide_container_value_el) return;

  if (!is_visible) {
    size_guide_container_value_el.style.visibility = `hidden`;
    return;
  }

  size_guide_container_value_el.style.left = `${position.x}px`;
  size_guide_container_value_el.style.top = `${position.y}px`;
  size_guide_container_value_el.innerHTML = size_value;
  size_guide_container_value_el.style.visibility = `visible`;
};

export const handleVectorEditorChangeSelector =
  ({ tab_el }) =>
  (event) => {
    const foreground_container_el = tab_el.querySelector(`.foreground_container`) ?? null;
    if (!foreground_container_el) return;

    const size_guide_container_el = foreground_container_el.querySelector(`.size_guide_container`) ?? null;
    if (!size_guide_container_el) return;

    const size_guide_container_value_el =
      size_guide_container_el?.querySelector(`.size_guide_container_value`) ?? null;
    if (!size_guide_container_value_el) return;

    if (!event.detail.in_ucs) {
      updateSizeGuideValue(size_guide_container_value_el, {}, '', false);
      return;
    }

    const { n_point, e_point, s_point, w_point } = event.detail.in_ucs;
    const { ne_point, se_point, sw_point, nw_point, is_show } = event.detail;

    const size_width = Math.round(
      Math.sqrt(Math.pow(w_point.x - e_point.x, 2) + Math.pow(w_point.y - e_point.y, 2)),
    );
    const size_height = Math.round(
      Math.sqrt(Math.pow(n_point.x - s_point.x, 2) + Math.pow(n_point.y - s_point.y, 2)),
    );
    const size_value = `${size_width} x ${size_height} mm`;

    const min_x = Math.min(ne_point.x, se_point.x, sw_point.x, nw_point.x);
    const max_x = Math.max(ne_point.x, se_point.x, sw_point.x, nw_point.x);
    const max_y = Math.max(ne_point.y, se_point.y, sw_point.y, nw_point.y);

    const left_container_el = foreground_container_el.closest('.left_container');
    const boundary_rect = left_container_el
      ? left_container_el.getBoundingClientRect()
      : { width: window.innerWidth, height: window.innerHeight };

    const size_guide_container_value_rect = size_guide_container_value_el.getBoundingClientRect();
    const x = Math.max(
      Math.min(
        (min_x + max_x) / 2 - (size_guide_container_value_rect.width ?? 0) / 2,
        boundary_rect.width - size_guide_container_value_rect.width,
      ),
      0,
    );
    const y = Math.max(
      max_y + (size_guide_container_value_rect.height ?? 0),
      64, // 48(모바일 헤더) + 16(마진)
    );

    updateSizeGuideValue(size_guide_container_value_el, { x, y }, size_value, is_show);
  };
